import React, {useCallback, useRef, useEffect} from 'react';
import {createPortal} from 'react-dom';
import cx from 'classnames';

import './DrawerBottomSheet.sass';

export const DrawerBottomSheet = ({
  children,
  isOpen,
  onClose,
  header,
  minHeightPercentage = 30,
  maxHeightPercentage = 70,
  className,
  initHeight,
}) => {
  const minHeight = window.innerHeight * (minHeightPercentage / 100);
  const maxHeight = window.innerHeight * (maxHeightPercentage / 100);

  const initialHeight = initHeight || minHeight;
  const dragRef = useRef(null);
  const startDragY = useRef(0);
  const startHeight = useRef(0);
  const sheetRef = useRef(null);
  const currentHeight = useRef(minHeight);

  const snapBackThreshold = minHeight * 0.25;

  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && sheetRef.current) {
      sheetRef.current.focus();
      sheetRef.current.style.height = initialHeight;
      sheetRef.current.style.opacity = '1';
      currentHeight.current = minHeight;
    }
  }, [isOpen, minHeight]);

  useEffect(() => {
    const handleResize = () => {
      if (sheetRef.current) {
        sheetRef.current.style.height = `${minHeight}px`;
        currentHeight.current = minHeight;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [minHeight]);

  const handleDragStart = useCallback((e) => {
    startDragY.current = e.touches ? e.touches[0].clientY : e.clientY;
    startHeight.current = currentHeight.current;
  }, []);

  const handleDrag = useCallback(
    (e) => {
      if (!startDragY.current || !sheetRef.current) return;

      const currentYPosition = e.touches ? e.touches[0].clientY : e.clientY;
      const diff = startDragY.current - currentYPosition;

      const newHeight = Math.min(Math.max(0, startHeight.current + diff), maxHeight);
      currentHeight.current = newHeight;

      sheetRef.current.style.height = `${newHeight}px`;

      if (newHeight < snapBackThreshold) {
        const opacityValue = Math.max(0, newHeight / snapBackThreshold);
        sheetRef.current.style.opacity = opacityValue.toString();
      } else {
        sheetRef.current.style.opacity = '1';
      }
    },
    [maxHeight, snapBackThreshold]
  );

  const handleDragEnd = useCallback(() => {
    if (!sheetRef.current) return;

    if (currentHeight.current < snapBackThreshold) {
      onClose();
      sheetRef.current.style.height = `${minHeight}px`;
      currentHeight.current = minHeight;
    } else if (currentHeight.current < minHeight) {
      sheetRef.current.style.height = `${minHeight}px`;
      currentHeight.current = minHeight;
    }

    sheetRef.current.style.opacity = '1';
    startDragY.current = 0;
  }, [minHeight, onClose]);

  useEffect(() => {
    const dragHandle = dragRef.current;

    dragHandle?.addEventListener('mousedown', handleDragStart);
    dragHandle?.addEventListener('touchstart', handleDragStart);
    window.addEventListener('mousemove', handleDrag);
    window.addEventListener('touchmove', handleDrag);
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('touchend', handleDragEnd);

    return () => {
      dragHandle?.removeEventListener('mousedown', handleDragStart);
      dragHandle?.removeEventListener('touchstart', handleDragStart);
      window.removeEventListener('mousemove', handleDrag);
      window.removeEventListener('touchmove', handleDrag);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [handleDrag, handleDragEnd, handleDragStart]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={sheetRef}
      role="dialog"
      aria-modal="true"
      aria-label="Drawer Bottom Sheet"
      tabIndex={-1}
      className={cx('drawer-bottom-sheet', className)}
      style={{
        height: initialHeight,
        opacity: 1,
      }}
    >
      <div ref={dragRef}>
        <div
          className="drawer-bottom-sheet__drag-handle"
          role="button"
          aria-label="Drag to resize or swipe down to close"
          tabIndex={0}
          data-qa-id="drawer-bottom-sheet-drag-handle"
        >
          <div className="drawer-bottom-sheet__drag-indicator" aria-hidden="true" />
        </div>
        {header}
      </div>
      {children}
    </div>,
    document.body
  );
};
